import React, { useState, useEffect, useCallback, useRef } from 'react';
import { CheckCircle, AlertCircle, Clock, FileText, Brain, Bell } from 'lucide-react';
import { Card } from './ui/Card';
import { Badge } from './ui/Badge';
import styles from './NotificationSystem.module.css';

interface NotificationEvent {
  type: 'extraction_start' | 'extraction_complete' | 'extraction_error' | 'rag_start' | 'rag_progress' | 'rag_complete' | 'processing_error' | 'connection_established';
  document_id: string;
  message: string;
  data?: any;
  timestamp: string;
}

interface NotificationToast {
  id: string;
  event: NotificationEvent;
  isVisible: boolean;
}

export const NotificationSystem: React.FC = () => {
  const [notifications, setNotifications] = useState<NotificationToast[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [hasEverConnected, setHasEverConnected] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const connectionAttemptsRef = useRef(0);
  const isConnectingRef = useRef(false);
  const userIdRef = useRef(`user_${Math.random().toString(36).substr(2, 9)}`); // Stable user ID
  const initializedRef = useRef(false); // Track if WebSocket has been initialized (StrictMode protection)

  const connectWebSocket = useCallback(() => {
    // StrictMode protection: prevent double connection
    if (initializedRef.current) {
      console.log('🚫 Connection blocked - WebSocket already initialized (StrictMode protection)');
      return;
    }

    // Prevent multiple connection attempts
    if (isConnectingRef.current || (wsRef.current && wsRef.current.readyState === WebSocket.CONNECTING)) {
      console.log('🚫 Connection attempt blocked - already connecting');
      return;
    }

    // Close existing connection if any
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    try {
      isConnectingRef.current = true;
      const wsBase = import.meta.env.PROD
        ? `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}`
        : (import.meta.env.VITE_WS_URL || 'ws://localhost:8000');
      const wsUrl = `${wsBase}/ws/notifications?user_id=${userIdRef.current}`;
      console.log(`🔗 Attempting WebSocket connection to: ${wsUrl}`);
      
      const ws = new WebSocket(wsUrl);
      
      ws.onopen = () => {
        console.log('✅ WebSocket connected successfully');
        setIsConnected(true);
        setHasEverConnected(true);
        connectionAttemptsRef.current = 0;
        isConnectingRef.current = false;
        initializedRef.current = true; // Mark as initialized on successful connection
      };
      
      ws.onmessage = (event) => {
        try {
          const notificationEvent: NotificationEvent = JSON.parse(event.data);
          console.log('📨 Received notification:', notificationEvent);
          
          // Create toast notification with unique ID
          const toast: NotificationToast = {
            id: `${notificationEvent.document_id}-${notificationEvent.type}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            event: notificationEvent,
            isVisible: true
          };
          
          setNotifications(prev => [toast, ...prev].slice(0, 10)); // Keep last 10
          
          // Auto-remove after duration for non-error notifications
          if (notificationEvent.type !== 'extraction_error' && notificationEvent.type !== 'processing_error') {
            // Hide after 5 seconds with animation
            setTimeout(() => {
              setNotifications(prev => 
                prev.map(n => n.id === toast.id ? { ...n, isVisible: false } : n)
              );
            }, 5000);
            
            // Actually remove from array after animation completes (5s + 300ms for animation)
            setTimeout(() => {
              setNotifications(prev => prev.filter(n => n.id !== toast.id));
            }, 5300);
          }
        } catch (err) {
          console.error('Failed to parse notification:', err);
        }
      };
      
      ws.onclose = (event) => {
        console.log(`🔌 WebSocket connection closed. Code: ${event.code}, Reason: ${event.reason}`);
        setIsConnected(false);
        isConnectingRef.current = false;
        
        // Only auto-reconnect if not manually closed and haven't exceeded max attempts
        if (event.code !== 1000 && connectionAttemptsRef.current < 5) {
          const delay = Math.min(1000 * Math.pow(2, connectionAttemptsRef.current), 30000);
          console.log(`🔄 Scheduling reconnection in ${delay}ms... (attempt ${connectionAttemptsRef.current + 1}/5)`);
          
          reconnectTimeoutRef.current = setTimeout(() => {
            connectionAttemptsRef.current++;
            connectWebSocket();
          }, delay);
        } else {
          console.log('🛑 Max reconnection attempts reached or connection manually closed');
        }
      };
      
      ws.onerror = (error) => {
        console.error('❌ WebSocket error:', error);
        isConnectingRef.current = false;
      };
      
      wsRef.current = ws;
    } catch (error) {
      console.error('Failed to create WebSocket:', error);
      isConnectingRef.current = false;
    }
  }, []); // Remove connectionAttempts dependency to prevent recreation

  useEffect(() => {
    console.log('🎬 NotificationSystem mounting - initializing WebSocket');
    
    // Only connect once when component mounts
    connectWebSocket();
    
    // Periodic cleanup of hidden notifications (every 10 seconds)
    const cleanupInterval = setInterval(() => {
      setNotifications(prev => prev.filter(n => n.isVisible));
    }, 10000);
    
    return () => {
      console.log('🧹 Cleaning up NotificationSystem...');
      
      // Clear cleanup interval
      clearInterval(cleanupInterval);
      
      // Clear reconnect timeout
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      
      // Close WebSocket connection
      if (wsRef.current) {
        wsRef.current.close(1000, 'Component unmounting');
        wsRef.current = null;
      }
      
      isConnectingRef.current = false;
      initializedRef.current = false; // Reset để cho phép kết nối lại khi remount
    };
  }, []); // Empty dependency array - only run on mount/unmount

  const removeNotification = (id: string) => {
    // First hide with animation
    setNotifications(prev => 
      prev.map(n => n.id === id ? { ...n, isVisible: false } : n)
    );
    
    // Then remove from array after animation completes
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 300);
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'extraction_start':
        return <FileText className="h-5 w-5 text-blue-600 dark:text-blue-400" />;
      case 'extraction_complete':
        return <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />;
      case 'extraction_error':
        return <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />;
      case 'rag_start':
        return <Brain className="h-5 w-5 text-purple-600 dark:text-purple-400" />;
      case 'rag_progress':
        return <Clock className="h-5 w-5 text-amber-600 dark:text-amber-400" />;
      case 'rag_complete':
        return <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />;
      case 'processing_error':
        return <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />;
      default:
        return <Bell className="h-5 w-5 text-gray-600 dark:text-gray-400" />;
    }
  };

  const getNotificationColor = (type: string) => {
    switch (type) {
      case 'extraction_start':
      case 'rag_start':
        return 'border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/50';
      case 'extraction_complete':
      case 'rag_complete':
        return 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950/50';
      case 'extraction_error':
      case 'processing_error':
        return 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950/50';
      case 'rag_progress':
        return 'border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/50';
      default:
        return 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/50';
    }
  };

  return (
    <>
      {/* Connection Status */}
      <div className="fixed top-4 right-4 z-50">
        <Badge 
          variant={isConnected ? "success" : "error"} 
          size="sm"
          className="shadow-lg backdrop-blur-sm"
        >
          {isConnected ? "🔗 Kết nối" : "🔌 Mất kết nối"}
        </Badge>
      </div>

      {/* Notification Toasts */}
      <div className="fixed top-16 right-4 z-50 space-y-3 w-80 max-w-[calc(100vw-2rem)] sm:w-96">
        {notifications
          .filter(n => n.isVisible)
          .map((notification) => (
            <Card 
              key={notification.id}
              className={`p-4 border-l-4 shadow-xl backdrop-blur-sm transition-all duration-300 ease-in-out transform ${
                notification.isVisible ? 'translate-x-0 opacity-100 scale-100' : 'translate-x-full opacity-0 scale-95'
              } ${getNotificationColor(notification.event.type)} dark:shadow-2xl hover:shadow-2xl dark:hover:shadow-3xl pointer-events-auto`}
            >
              <div className="flex items-start gap-3">
                {getNotificationIcon(notification.event.type)}
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {notification.event.message}
                    </p>
                    
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        removeNotification(notification.id);
                      }}
                      className="ml-2 flex-shrink-0 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-gray-400 rounded"
                      aria-label="Đóng thông báo"
                    >
                      <svg 
                        className="w-5 h-5" 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                          strokeWidth={2} 
                          d="M6 18L18 6M6 6l12 12" 
                        />
                      </svg>
                    </button>
                  </div>
                  
                  {/* Document ID */}
                  {notification.event.document_id && (
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                      ID: {notification.event.document_id}
                    </p>
                  )}
                  
                  {/* Progress bar for RAG progress */}
                  {notification.event.type === 'rag_progress' && notification.event.data && (
                    <div className="mt-2">
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div 
                          className="bg-blue-600 dark:bg-blue-500 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${notification.event.data.percentage || 0}%` }}
                        />
                      </div>
                      <p className="text-xs text-gray-700 dark:text-gray-300 mt-1">
                        {notification.event.data.step}: {notification.event.data.progress}/{notification.event.data.total}
                      </p>
                    </div>
                  )}
                  
                  {/* Summary for completion */}
                  {notification.event.type === 'rag_complete' && notification.event.data && (
                    <div className="mt-2 text-xs text-gray-700 dark:text-gray-300">
                      <p>📊 {notification.event.data.chunks_count} chunks, {notification.event.data.entities_count} entities</p>
                      <p>⏱️ Thời gian: {notification.event.data.total_time?.toFixed(1)}s</p>
                    </div>
                  )}
                  
                  {/* Timestamp */}
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {new Date(notification.event.timestamp).toLocaleTimeString('vi-VN')}
                  </p>
                </div>
              </div>
            </Card>
          ))}
      </div>
    </>
  );
};

export default NotificationSystem;